// src/pages/Reports/IssuanceReporting/ReconciliationDiscrepancy/AgentReconciliationTab.tsx
// RPT-009 (README.md mục 7) — đối soát đại lý theo kỳ phát hành. BẮT BUỘC chọn 1 kỳ trước khi tải
// (danh sách gồm CẢ đại lý chưa xác nhận đối soát, tính runtime — xem GetPaginatedAgentReconciliationsQuery).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPaginatedAgentReconciliationsApi } from 'helpers/fakebackend_helper';
import { AGENT_RECONCILIATION } from 'helpers/url_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { cleanParams, formatPercent, formatQty, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { AgentReconciliation, INITIAL_REPORT_PAGINATION, ReportPaginationState } from '../shared/types';
import ConfirmReconciliationModal from './ConfirmReconciliationModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATUS_OPTIONS = [
  { value: 1, label: st('chua_doi_soat_label') },
  { value: 2, label: st('da_doi_soat_label') },
];

const AgentReconciliationTab: React.FC = () => {
  const location = useLocation();
  const [planId, setPlanId] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<AgentReconciliation[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);
  const [confirmTarget, setConfirmTarget] = useState<AgentReconciliation | null>(null);

  useEffect(() => { loadAgentsWithCache().then(setAgents); }, []);

  const buildFilterParams = useCallback(
    () => ({ IssuancePlanId: planId || undefined, Status: status || undefined }),
    [planId, status],
  );

  const loadReport = useCallback(async () => {
    if (!planId) {
      setRows([]);
      setPagination(INITIAL_REPORT_PAGINATION);
      return;
    }
    setLoading(true);
    try {
      const res = await getPaginatedAgentReconciliationsApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_danh_sach_doi_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize, planId]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, status]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    if (!planId) return;
    setExporting(format);
    try {
      await downloadReportExport(AGENT_RECONCILIATION, buildFilterParams(), format, 'DoiSoatDaiLy');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  const canConfirm = PageActionPermission.canDisplay(location.pathname, 'button-confirm-reconciliation');

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')} <span className="text-danger">*</span></Label>
          <IssuancePlanSelect requireSelection value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('trang_thai_column')}</Label>
          <Input type="select" value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }} disabled={!planId}>
            <option value="">{st('tat_ca_option')}</option>
            {STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
          </Input>
        </Col>
        <Col md={6} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!planId || !!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!planId || !!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {!planId ? (
        <Alert color="info" className="mb-0">{st('vui_long_chon_ky_phat_hanh_message_2')}</Alert>
      ) : loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('dai_ly_label')}</th>
                <th className="text-end">{st('da_giao_column')}</th>
                <th className="text-end">{st('thu_hoi_column')}</th>
                <th className="text-end">{st('da_ban_column')}</th>
                <th className="text-end">{st('ty_le_tieu_thu_column')}</th>
                <th className="text-end">{st('chenh_lech_column')}</th>
                <th>{st('trang_thai_column')}</th>
                {canConfirm && <th style={{ width: 120 }}>{st('thao_tac_column')}</th>}
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={canConfirm ? 9 : 8} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.agentId}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>
                    <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                    <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                  </td>
                  <td className="text-end">{formatQty(row.deliveredQuantity)}</td>
                  <td className="text-end">{formatQty(row.recalledQuantity)}</td>
                  <td className="text-end text-success">{formatQty(row.soldQuantity)}</td>
                  <td className="text-end">{formatPercent(row.consumptionRate)}</td>
                  <td className={`text-end ${row.discrepancyQuantity !== 0 ? 'text-danger' : ''}`}>{formatQty(row.discrepancyQuantity)}</td>
                  <td>
                    <span className={`badge bg-${row.status === 2 ? 'success' : 'warning'}-subtle text-${row.status === 2 ? 'success' : 'warning'}`}>
                      {row.statusName}
                    </span>
                  </td>
                  {canConfirm && (
                    <td>
                      <Button
                        color="success"
                        outline
                        size="sm"
                        data-action-code="button-confirm-reconciliation"
                        data-action-name="Xác nhận đối soát"
                        onClick={() => setConfirmTarget(row)}
                      >
                        {st('xac_nhan_button')}
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}

      <ConfirmReconciliationModal
        isOpen={!!confirmTarget}
        planId={Number(planId)}
        target={confirmTarget}
        agentMap={agentMap}
        onClose={() => setConfirmTarget(null)}
        onConfirmed={() => { setConfirmTarget(null); loadReport(); }}
      />
    </>
  );
};

export default AgentReconciliationTab;
